import * as React from "react";

import { Input } from "@/components/ui/input";

/**
 * A numeric `<Input>` that holds its own text buffer so the field can be
 * emptied and edited freely.
 *
 * Binding `value={someNumber}` directly to a controlled input has two nasty
 * symptoms when the value is `0`:
 *   1. Backspacing the "0" immediately snaps it back (because `Number("")` is
 *      `0`), so it can never be cleared.
 *   2. Typing into a literal "0" produces "10" / "01" instead of replacing it.
 *
 * This component fixes both by:
 *   - Keeping the displayed text in local state. While the field is focused the
 *     buffer is the source of truth and is never overwritten by the prop, so
 *     editing is never interrupted. On blur (and on external value changes
 *     while unfocused) it resyncs to the canonical number.
 *   - Rendering `0` as an empty field with a placeholder, so there is no stuck
 *     "0" character to type around.
 *
 * The parent only ever receives a number via `onChange` — empty parses to `0`
 * (override with `parse` to clamp, e.g. `raw => Math.max(1, Number(raw) || 1)`).
 */
export interface NumberInputProps
  extends Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "type"> {
  value: number;
  onChange: (value: number) => void;
  /**
   * Convert the raw input string to the numeric value. Defaults to
   * `Number(raw) || 0`, clamped to be no less than `min` (which itself
   * defaults to `0`, so these fields never go negative).
   */
  parse?: (raw: string) => number;
}

const toText = (value: number) => (value === 0 ? "" : String(value));

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    { value, onChange, parse, placeholder = "0", min = 0, onFocus, onBlur, ...props },
    ref,
  ) => {
    const [text, setText] = React.useState(() => toText(value));
    const [focused, setFocused] = React.useState(false);

    // Default parse keeps the value at or above `min` (0 by default) so the
    // native stepper's down-arrow — and typed-in negatives — can't go negative.
    const floor = typeof min === "string" ? Number(min) : min;
    const parseValue =
      parse ??
      ((raw: string) => {
        const n = Number(raw) || 0;
        return Number.isFinite(floor) ? Math.max(floor, n) : n;
      });

    // Reflect external value changes only when the user isn't actively editing,
    // so an in-progress entry (e.g. a half-typed "1.") is never clobbered.
    React.useEffect(() => {
      if (!focused) setText(toText(value));
    }, [value, focused]);

    return (
      <Input
        {...props}
        ref={ref}
        type="number"
        min={min}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parseValue(e.target.value));
        }}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
      />
    );
  },
);
NumberInput.displayName = "NumberInput";
